/**
 * API Cache Service
 * Prevents duplicate API calls by caching responses for a specified duration
 */

class ApiCache {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
  }

  /**
   * Generate a cache key from request config
   */
  getCacheKey(url, method = 'GET', params = {}, data = {}) {
    const paramsStr = JSON.stringify(params);
    const dataStr = JSON.stringify(data);
    return `${method}:${url}:${paramsStr}:${dataStr}`;
  }

  /**
   * Get cached response if available and not expired
   */
  get(key, maxAge = 60000) { // Default 1 minute
    const cached = this.cache.get(key);
    if (!cached) return null;

    const now = Date.now();
    if (now - cached.timestamp > maxAge) {
      this.cache.delete(key);
      return null;
    }

    return cached.data;
  }

  /**
   * Set cache with timestamp
   */
  set(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  /**
   * Check if request is already pending
   */
  isPending(key) {
    return this.pendingRequests.has(key);
  }

  /**
   * Get pending request promise
   */
  getPending(key) {
    return this.pendingRequests.get(key);
  }

  /**
   * Set pending request
   */
  setPending(key, promise) {
    this.pendingRequests.set(key, promise);
    promise.finally(() => {
      this.pendingRequests.delete(key);
    });
  }

  /**
   * Clear cache for a specific key or all cache
   */
  clear(key = null) {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }

  /**
   * Clear expired cache entries
   */
  clearExpired(maxAge = 60000) {
    const now = Date.now();
    for (const [key, value] of this.cache.entries()) {
      if (now - value.timestamp > maxAge) {
        this.cache.delete(key);
      }
    }
  }
}

// Singleton instance
const apiCache = new ApiCache();

// Clear expired cache every 5 minutes
setInterval(() => {
  apiCache.clearExpired();
}, 5 * 60 * 1000);

export default apiCache;

